import { EventOption } from '@frontend/sports/betting-offer/feature/model';
import { Odds } from '@frontend/sports/odds/feature';
import { createAction, props } from '@ngrx/store';

import BetslipActions from '../../base/store/actions';
import { BetslipPick } from '../../core/picks/betslip-pick';
import { BetslipV1Pick } from '../../core/picks/betslip-v1-pick';
import { BetslipV2Pick } from '../../core/picks/betslip-v2-pick';
import { PickId } from '../../core/picks/pick-id';

export interface IAddTeaserPickPayload {
    pick: BetslipPick;
    selectedOption: EventOption;
    isBalancedMarket?: boolean;
    order?: number;
}

export interface IAddTeaserGroupLegPayload {
    newPick: BetslipPick;
    order: number;
}

export class BetslipTeaserActions {
    static readonly ACTION_SCHEMA = `${BetslipActions.ACTION_SCHEMA}/teaser`;

    private static readonly ADD_UPDATE_PICK = `${BetslipTeaserActions.ACTION_SCHEMA}/ADD_UPDATE_PICK`;

    private static readonly ADD_GROUP_PICK_LEG = `${BetslipTeaserActions.ACTION_SCHEMA}/ADD_GROUP_PICK_LEG`;

    private static readonly UPDATE_BALANCE_PICK = `${BetslipTeaserActions.ACTION_SCHEMA}/UPDATE_BALANCE_PICK`;

    private static readonly START_REMOVE_ALL_PICKS = `${BetslipTeaserActions.ACTION_SCHEMA}/START_REMOVE_ALL_PICKS`;

    private static readonly REMOVE_PICK = `${BetslipTeaserActions.ACTION_SCHEMA}/REMOVE_PICK`;

    private static readonly UPDATE_TOTAL_ODDS = `${BetslipTeaserActions.ACTION_SCHEMA}/UPDATE_TOTAL_ODDS`;

    private static readonly SET_TEASE_PICKS = `${BetslipTeaserActions.ACTION_SCHEMA}/SET_TEASE_PICKS`;

    private static readonly SET_PLACEBLOCKING = `${BetslipTeaserActions.ACTION_SCHEMA}/SET_PLACEBLOCKING`;

    private static readonly REMOVE_PICKS = `${BetslipTeaserActions.ACTION_SCHEMA}/REMOVE_PICKS`;

    private static readonly REMOVE_PLACED_PICKS = `${BetslipTeaserActions.ACTION_SCHEMA}/REMOVE_PLACED_PICKS`;

    private static readonly REMOVE_LOCKEDORCLOSED_PICKS = `${BetslipTeaserActions.ACTION_SCHEMA}/REMOVE_LOCKEDORCLOSED_PICKS`;

    private static readonly TEASED_PICK_CHANGED = `${BetslipTeaserActions.ACTION_SCHEMA}/TEASED_PICK_CHANGED`;

    private static readonly REMOVE_PICKS_FINISHED = `${BetslipTeaserActions.ACTION_SCHEMA}/REMOVE_PICK_FINISHED`;

    private static readonly UPDATE_ALTERNATE_PICK = `${BetslipTeaserActions.ACTION_SCHEMA}/UPDATE_ALTERNATE_PICK`;

    private static readonly ACCEPT_CURRENT_PRICE = `${BetslipTeaserActions.ACTION_SCHEMA}/ACCEPT_CURRENT_PRICE`;

    private static readonly SET_TEASER_BET_STAKE = `${BetslipTeaserActions.ACTION_SCHEMA}/SET_TEASER_BET_STAKE`;

    private static readonly TOGGLE_PICK = `${BetslipTeaserActions.ACTION_SCHEMA}/TOGGLE_PICK`;

    private static readonly TOGGLE_ALL_PICKS = `${BetslipTeaserActions.ACTION_SCHEMA}/TOGGLE_ALL_PICKS`;

    private static readonly SET_TEASER_BET_REWARD_TOKEN = `${BetslipTeaserActions.ACTION_SCHEMA}/SET_TEASER_BET_REWARD_TOKEN`;

    private static readonly CLICK_EXPAND_LINEAR_TEASER_BET = `${BetslipTeaserActions.ACTION_SCHEMA}/CLICK_EXPAND_LINEAR_TEASER_BET`;

    private static readonly TOGGLE_EXPAND_LINEAR_TEASER_BET = `${BetslipTeaserActions.ACTION_SCHEMA}/TOGGLE_EXPAND_LINEAR_TEASER_BET`;
    private static readonly UPDATE_PICK_ORDER = `${BetslipTeaserActions.ACTION_SCHEMA}/UPDATE_PICK_ORDER`;
    /**
     * Complete loading of new pick to the teaser.
     */
    static addUpdatePick = createAction(BetslipTeaserActions.ADD_UPDATE_PICK, props<{ payload: IAddTeaserPickPayload }>());

    static addGroupPickLeg = createAction(BetslipTeaserActions.ADD_GROUP_PICK_LEG, props<IAddTeaserGroupLegPayload>());

    static updateBalancePick = createAction(
        BetslipTeaserActions.UPDATE_BALANCE_PICK,
        props<{ payload: { pickId: string; isNonBalanceMarket: boolean } }>(),
    );

    static startRemoveAllPicks = createAction(BetslipTeaserActions.START_REMOVE_ALL_PICKS, props<{ removeFromOtherBetTypes: boolean }>());

    static removePick = createAction(BetslipTeaserActions.REMOVE_PICK, props<{ pickId: string }>());

    static removePicks = createAction(BetslipTeaserActions.REMOVE_PICKS, props<{ pickIds: string[]; removedOrders?: number[] }>());

    static removeLockedOrClosedPicks = createAction(BetslipTeaserActions.REMOVE_LOCKEDORCLOSED_PICKS);

    static removePlacedPicks = createAction(BetslipTeaserActions.REMOVE_PLACED_PICKS, props<{ removeFromOtherBetTypes: boolean }>());

    /**
     * will update the prices upon any action on teaser picks
     */
    static updateTeaserPrices = createAction(
        BetslipTeaserActions.UPDATE_TOTAL_ODDS,
        props<{
            odds: Odds;
            priceVersionId: number;
            selectedPoints: number;
            picks: { [pickId: string]: { oldPoints: number; newPoints: number } };
        }>(),
    );

    /**
     * tease picks in a teaser with selected key point
     */
    static setTeasePicks = createAction(BetslipTeaserActions.SET_TEASE_PICKS, props<{ keypoint: number }>());

    /**
     * update teaser error state
     */
    static setPlaceBlocking = createAction(BetslipTeaserActions.SET_PLACEBLOCKING, props<{ sportId: number; placeBlocking: boolean }>());

    /**
     * add alternate pick of teaser pick
     */

    static removePicksFinished = createAction(BetslipTeaserActions.REMOVE_PICKS_FINISHED, props<{ pickIds: PickId[] }>());

    static updateAlternatePick = createAction(
        BetslipTeaserActions.UPDATE_ALTERNATE_PICK,
        props<{ alternatePick: BetslipV1Pick | BetslipV2Pick; basePickId: string }>(),
    );

    static acceptCurrentPrice = createAction(
        BetslipTeaserActions.ACCEPT_CURRENT_PRICE,
        props<{ alternatePickId: PickId; isManuallyAccepted: boolean; pickId: PickId }>(),
    );

    /**
     * Teased option changed for a selection
     */
    static teasedPickChanged = createAction(
        BetslipTeaserActions.TEASED_PICK_CHANGED,
        props<{ oldPick: BetslipPick | undefined; newPick: BetslipPick }>(),
    );

    static setTeaserBetStake = createAction(
        BetslipTeaserActions.SET_TEASER_BET_STAKE,
        props<{ stake: number | null; stakeStr: string | null; silent?: boolean }>(),
    );

    /**
     * select / deselect a pick
     **/
    static togglePick = createAction(BetslipTeaserActions.TOGGLE_PICK, props<{ pickId: string }>());

    static toggleAllPicks = createAction(BetslipTeaserActions.TOGGLE_ALL_PICKS, props<{ select: boolean }>());

    static setTeaserBetRewardToken = createAction(BetslipTeaserActions.SET_TEASER_BET_REWARD_TOKEN, props<{ rewardTokenId: string | null }>());

    static clickExpandLinearTeaserBet = createAction(BetslipTeaserActions.CLICK_EXPAND_LINEAR_TEASER_BET, props<{ isExpanded: boolean }>());

    static toggleExpandLinearTeaserBet = createAction(BetslipTeaserActions.TOGGLE_EXPAND_LINEAR_TEASER_BET, props<{ isExpanded: boolean }>());

    static updateOrder = createAction(BetslipTeaserActions.UPDATE_PICK_ORDER, props<{ pickId: string; order: number }>());
}
